import { SendAlt, Time } from '@carbon/icons-react';
import type { GetAssessmentQuery } from '@gql/gql-types';
import {
  Alert,
  alpha,
  Box,
  Button,
  Snackbar,
  Toolbar,
  Typography,
  useTheme,
} from '@mui/material';
import { getInterviewApiUrl } from '@utils/environment';
import { getTimeColor } from '@utils/format-time';
import { noop } from 'lodash';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useInterviewCompletion } from '../../hooks/use-interview-completion';
import { useInterviewHooks } from '../../hooks/use-interview-hooks';
import { useScreenRecorderContext } from '../../hooks/use-screen-recorder-context';
import {
  InterviewCompleting,
  InterviewErrorLoading,
  InterviewNetworkConnectionLost,
  InterviewResumeError,
  InterviewScreenSharingRequired,
  InterviewZaraBusy,
} from '../interview/components/interview-state-views';
import { useAudioFlow } from '../interview/hooks/use-audio-flow';
import useAudioRecorder from '../interview/hooks/use-audio-recorder';
import useAvService from '../interview/hooks/use-av-service';
import useChatClient from '../interview/hooks/use-chat-client';
import { useClosePageAlert } from '../interview/hooks/use-close-page-alert';
import useTimeLeft from '../interview/hooks/use-time-left';
import useVideoUploadClient from '../interview/hooks/use-video-upload-client';
import type { InteractionState, NetworkState } from '../interview/types';
import { VideoAndZaraOverlay } from '../live-coding/video-and-zara-overlay';
import { FileDiffCard } from './file-diff-card';
import { FileSidebar } from './file-sidebar';
import { SubmitConfirmationModal } from './submit-confirmation-modal';
import type { CodeReviewTemplateAttributes, FileToReview } from './types';
import { useCodeReviewComments } from './use-code-review-comments';
import { formatTimeRemaining } from './utils';

// Share a screenshot every 3 seconds
const SCREENSHOT_SHARE_INTERVAL_MS = 3 * 1000;

type CodeReviewMainProps = {
  interviewId: string;
  accessToken: string;
  assessment: NonNullable<GetAssessmentQuery['assessment']>;
  requestedProvider?: 'aurora' | 'polaris' | 'zenith';
  onStarted: () => void;
  onCompleted: () => void;
  updateAssessmentCache: (startedAt: Date) => void;
};

const CodeReviewMain = ({
  interviewId,
  accessToken,
  assessment,
  requestedProvider,
  onStarted,
  onCompleted,
  updateAssessmentCache,
}: CodeReviewMainProps) => {
  const audioFlow = useAudioFlow(interviewId);
  const theme = useTheme();
  const videoRef = useRef<HTMLVideoElement | null>(null);

  // Interview state
  const [interactionState, setInteractionState] =
    useState<InteractionState>('idle');
  const [networkState, setNetworkState] = useState<NetworkState>('stable');
  const [shouldStartCompleting, setShouldStartCompleting] = useState(false);
  const [isCompleting, setIsCompleting] = useState(false);
  const [isComplete, setIsComplete] = useState(false);
  const [showConnectionRecoveredMessage, setShowConnectionRecoveredMessage] =
    useState(false);
  const [zaraBusyError, setZaraBusyError] = useState(false);
  const [resumeError, setResumeError] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showSubmitModal, setShowSubmitModal] = useState(false);

  // File sidebar state
  const [selectedFile, setSelectedFile] = useState<string | undefined>();
  const fileRefs = useRef<Record<string, HTMLDivElement | null>>({});

  // Comments management via custom hook
  const {
    comments,
    handleAddComment,
    handleEditComment,
    handleDeleteComment,
    handleRetryComment,
    errorSnackbar,
    clearError,
  } = useCodeReviewComments();

  // Extract template attributes and language
  const templateAttrs = assessment.template
    .filteredAttributes as CodeReviewTemplateAttributes | null;

  if (!assessment.args?.programmingLanguage) {
    throw new Error('No programming language found in assessment args');
  }

  const language = assessment.args.programmingLanguage;

  const files: FileToReview[] = useMemo(() => {
    if (!templateAttrs?.perLanguageAttributes || !language) {
      return [];
    }
    return templateAttrs.perLanguageAttributes[language]?.files || [];
  }, [templateAttrs?.perLanguageAttributes, language]);

  const sendPlaybackCompleteRef = useRef<() => void>(() => {});
  const sendPlaybackCompleteStable = useCallback(() => {
    sendPlaybackCompleteRef.current();
  }, []);

  // AV Service
  const {
    play,
    interrupt,
    startRecording,
    stopRecording,
    videoStream,
    isPlaying,
    analyserNode,
    destinationAudioStream,
  } = useAvService({
    audioFlow,
    onVideoChunk: (chunk) => queueChunk(chunk),
    onPlaybackComplete: () => {
      sendPlaybackCompleteRef.current();
      if (interactionState !== 'recording') {
        setInteractionState('idle');
      }
    },
  });

  const {
    onResponseAudio,
    onResponseInterrupted,
    onConnectionUnstable,
    onConnectionLost,
    onConnectionRecovered,
    onChatClientError,
    onChatClientClosed,
    onChatClientComplete,
    onChatClientCompleted,
  } = useInterviewHooks({
    interviewId,
    setInteractionState,
    onCompleted,
    play,
    interrupt,
    sendPlaybackComplete: sendPlaybackCompleteStable,
    stopRecording,
    setNetworkState,
    setShowConnectionRecoveredMessage,
    setZaraBusyError,
    setResumeError,
    setShouldStartCompleting,
    setIsComplete,
  });

  const {
    isScreenSharingActive,
    setChunkHandler,
    startScreenRecording,
    stopScreenRecording,
    captureFrame,
  } = useScreenRecorderContext();

  const {
    isStarted: isInterviewStarted,
    videoUploadUrl,
    screenRecordingUploadUrl,
    startedAt,
    duration,
    inputSampleRate,
    appendInputAudio,
    appendInputImage,
    completeInterview,
    sendPlaybackComplete,
  } = useChatClient({
    audioFlow,
    apiUrl: getInterviewApiUrl() || 'missing-interview-api-url',
    interviewId,
    accessToken,
    requestedProvider,
    onResponseAudio: isScreenSharingActive ? onResponseAudio : noop,
    onResponseInterrupted: onResponseInterrupted,
    onConnectionUnstable: isScreenSharingActive ? onConnectionUnstable : noop,
    onConnectionLost: isScreenSharingActive ? onConnectionLost : noop,
    onConnectionRecovered: isScreenSharingActive ? onConnectionRecovered : noop,
    onClosed: onChatClientClosed,
    onError: onChatClientError,
    onStarted: onStarted,
    onComplete: onChatClientComplete,
    onCompleted: onChatClientCompleted,
    updateAssessmentCache: updateAssessmentCache,
  });

  useEffect(() => {
    audioFlow.playbackSuppressed = !isScreenSharingActive;
  }, [audioFlow, isScreenSharingActive]);

  useEffect(() => {
    sendPlaybackCompleteRef.current = sendPlaybackComplete;
  }, [sendPlaybackComplete]);

  const { queueChunk, finishUpload: finishUserCameraUpload } =
    useVideoUploadClient({
      videoUploadUrl,
      isUploading: isInterviewStarted,
    });

  const {
    queueChunk: queueScreenRecordingChunk,
    finishUpload: finishScreenRecordingUpload,
    isReadyToUpload: isScreenRecordingReadyToUpload,
  } = useVideoUploadClient({
    videoUploadUrl: screenRecordingUploadUrl,
    isUploading: isInterviewStarted,
  });

  const { cleanupAndCompleteInterview, forceCompleteInterview } =
    useInterviewCompletion({
      setIsCompleting,
      stopRecording,
      finishUserCameraUpload,
      stopScreenRecording,
      finishScreenRecordingUpload,
      completeInterview,
    });

  const handleSubmit = useCallback(async () => {
    setShowSubmitModal(false);
    setIsSubmitting(true);
    await cleanupAndCompleteInterview();
    setIsSubmitting(false);
    onCompleted();
  }, [cleanupAndCompleteInterview, onCompleted]);

  // Scroll to file (used by sidebar and search navigation)
  const scrollToFile = useCallback((filename: string) => {
    setSelectedFile(filename);
    const fileRef = fileRefs.current[filename];
    if (fileRef) {
      fileRef.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }, []);

  // Start recording when interview starts
  useEffect(() => {
    if (isInterviewStarted && isScreenSharingActive) {
      startRecording();
      setInteractionState('recording');
    } else {
      setInteractionState('idle');
    }
  }, [isInterviewStarted, isScreenSharingActive, startRecording]);

  // Audio recording
  useAudioRecorder({
    audioFlow,
    isRecording:
      isInterviewStarted && networkState !== 'lost' && isScreenSharingActive,
    onAudioChunk: appendInputAudio,
    inputSampleRate,
  });

  // Screen recording
  useEffect(() => {
    if (
      isScreenRecordingReadyToUpload &&
      isInterviewStarted &&
      networkState !== 'lost' &&
      isScreenSharingActive
    ) {
      startScreenRecording(destinationAudioStream ?? undefined);
      setChunkHandler(queueScreenRecordingChunk);
    } else {
      setChunkHandler(undefined);
    }
  }, [
    isScreenRecordingReadyToUpload,
    isInterviewStarted,
    networkState,
    isScreenSharingActive,
    setChunkHandler,
    queueScreenRecordingChunk,
    startScreenRecording,
    destinationAudioStream,
  ]);

  // Frame capture for AI
  const frameCaptureIntervalRef = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    if (
      !isInterviewStarted ||
      networkState === 'lost' ||
      !isScreenSharingActive
    ) {
      return;
    }

    const handleCaptureFrame = async () => {
      const frameDataUrl = await captureFrame();
      if (frameDataUrl) {
        appendInputImage(frameDataUrl);
      }
    };

    const firstFrameTimeout = setTimeout(async () => {
      await handleCaptureFrame();
      frameCaptureIntervalRef.current = setInterval(async () => {
        await handleCaptureFrame();
      }, SCREENSHOT_SHARE_INTERVAL_MS);
    }, 5000);

    return () => {
      clearTimeout(firstFrameTimeout);
      if (frameCaptureIntervalRef.current) {
        clearInterval(frameCaptureIntervalRef.current);
        frameCaptureIntervalRef.current = null;
      }
    };
  }, [
    isInterviewStarted,
    networkState,
    isScreenSharingActive,
    captureFrame,
    appendInputImage,
  ]);

  const timeLeft = useTimeLeft({ startedAt, duration });

  // Video stream
  const currentVideoRef = videoRef.current;
  useEffect(() => {
    if (currentVideoRef) {
      currentVideoRef.srcObject = videoStream;
    }
  }, [videoStream, currentVideoRef]);

  // Auto-complete when time runs out
  useEffect(() => {
    const readyToFinish = interactionState !== 'aiSpeaking' && !isPlaying;
    if (shouldStartCompleting && readyToFinish) {
      cleanupAndCompleteInterview();
    }
  }, [
    interactionState,
    isPlaying,
    shouldStartCompleting,
    cleanupAndCompleteInterview,
  ]);

  useClosePageAlert({ showAlert: !isComplete });

  const timeColor = getTimeColor(timeLeft, {
    success: theme.palette.success.main,
    warning: theme.palette.warning.main,
    error: theme.palette.error.main,
  });

  // Error states
  if (!isScreenSharingActive && !isCompleting && !isComplete) {
    return <InterviewScreenSharingRequired />;
  }

  if (zaraBusyError) {
    return <InterviewZaraBusy />;
  }

  if (resumeError) {
    return <InterviewResumeError />;
  }

  if (interactionState === 'error') {
    return <InterviewErrorLoading />;
  }

  if (isCompleting) {
    return <InterviewCompleting />;
  }

  if (networkState === 'lost') {
    return <InterviewNetworkConnectionLost />;
  }

  // Loading state
  if (!isInterviewStarted) {
    return (
      <Box sx={{ height: '100vh', display: 'flex', flexDirection: 'column' }}>
        <VideoAndZaraOverlay
          videoRef={videoRef}
          isInterviewStarted={isInterviewStarted}
          interactionState={interactionState}
          analyserNode={analyserNode}
          assessmentId={interviewId}
          onUserEndedInterview={forceCompleteInterview}
        />
      </Box>
    );
  }

  return (
    <Box sx={{ height: '100vh', display: 'flex', flexDirection: 'column' }}>
      {/* Network alerts */}
      <Snackbar
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        open={networkState === 'unstable'}
      >
        <Alert severity='warning'>
          Unstable network connection, attempting to reconnect...
        </Alert>
      </Snackbar>
      <Snackbar
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        open={showConnectionRecoveredMessage}
        autoHideDuration={3000}
      >
        <Alert severity='success'>Connection recovered</Alert>
      </Snackbar>

      {/* Error snackbar for comment operations */}
      <Snackbar
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        open={Boolean(errorSnackbar)}
        autoHideDuration={5000}
        onClose={clearError}
      >
        <Alert severity='error' onClose={clearError}>
          {errorSnackbar}
        </Alert>
      </Snackbar>

      {/* Main content - Full width diff viewer */}
      <Box
        sx={{
          flexGrow: 1,
          overflow: 'hidden',
          backgroundColor: theme.palette.background.default,
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        {/* Header with timer, search, and submit */}
        <Toolbar
          sx={{
            justifyContent: 'space-between',
            backgroundColor: theme.palette.background.default,
            borderBottom: `1px solid ${theme.palette.divider}`,
            flexShrink: 0,
            minHeight: 48,
            px: 2,
          }}
        >
          <Typography variant='subtitle2' color='text.secondary'>
            Review the below pull request by adding comments.
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Time size={20} style={{ color: timeColor }} />
              <Typography
                variant='h6'
                sx={{
                  fontWeight: 600,
                  color: timeColor,
                  fontFamily: 'monospace',
                }}
              >
                {formatTimeRemaining(timeLeft ?? 0)}
              </Typography>
            </Box>

            <Button
              variant='contained'
              startIcon={<SendAlt size={16} />}
              onClick={() => setShowSubmitModal(true)}
              disabled={isSubmitting}
              sx={{
                textTransform: 'none',
                fontWeight: 600,
                backgroundColor: theme.palette.success.main,
                '&:hover': {
                  backgroundColor: theme.palette.success.dark,
                },
              }}
            >
              {isSubmitting ? 'Submitting...' : 'Submit Review'}
            </Button>
          </Box>
        </Toolbar>

        {/* File sidebar + Diff area */}
        <Box
          sx={{
            flexGrow: 1,
            display: 'flex',
            overflow: 'hidden',
          }}
        >
          {/* File sidebar */}
          <FileSidebar
            files={files}
            comments={comments}
            onFileClick={scrollToFile}
            selectedFile={selectedFile}
          />

          {/* Scrollable file diff list */}
          <Box
            sx={{
              flexGrow: 1,
              overflow: 'auto',
              p: 2,
              backgroundColor: alpha(theme.palette.background.default, 0.5),
            }}
          >
            {files.length > 0 ? (
              files.map((file) => (
                <FileDiffCard
                  key={file.filename}
                  ref={(el) => {
                    fileRefs.current[file.filename] = el;
                  }}
                  id={`file-${file.filename.replace(/[^a-zA-Z0-9]/g, '-')}`}
                  file={file}
                  comments={comments.filter(
                    (c) => c.filename === file.filename,
                  )}
                  onAddComment={handleAddComment}
                  onEditComment={handleEditComment}
                  onDeleteComment={handleDeleteComment}
                  onRetryComment={handleRetryComment}
                  isDisabled={isSubmitting}
                  defaultExpanded
                />
              ))
            ) : (
              <Box
                sx={{
                  height: '100%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <Typography color='text.secondary'>
                  No files to review
                </Typography>
              </Box>
            )}
          </Box>
        </Box>
      </Box>

      {/* Video overlay */}
      <VideoAndZaraOverlay
        videoRef={videoRef}
        isInterviewStarted={isInterviewStarted}
        interactionState={interactionState}
        analyserNode={analyserNode}
        assessmentId={interviewId}
        onUserEndedInterview={forceCompleteInterview}
      />

      {/* Submit confirmation modal */}
      <SubmitConfirmationModal
        open={showSubmitModal}
        onClose={() => setShowSubmitModal(false)}
        onSubmit={handleSubmit}
        commentCount={comments.length}
        isSubmitting={isSubmitting}
      />
    </Box>
  );
};

export default CodeReviewMain;
